// Maps a page URL to the tool it belongs to. Runs in the browser (to show the user
// which tool they are reporting for) and on the server (authoritative routing).
//
// Pattern syntax (one string per pattern):
//   wolke.example.org              exact host, any path
//   *.example.org                  any subdomain of example.org (not example.org itself)
//   example.org/antraege           host + path prefix (segment boundary: /antraege, /antraege/…)
//   localhost:4173/demo/wolke.html host with port + path

export interface ParsedPattern {
  host: string;
  wildcard: boolean;
  port?: string;
  path: string;
}

export function parsePattern(pattern: string): ParsedPattern {
  let rest = pattern.trim().toLowerCase().replace(/^https?:\/\//, '');
  const slash = rest.indexOf('/');
  let path = '/';
  if (slash >= 0) {
    path = rest.slice(slash).replace(/\/\*?$/, '') || '/';
    rest = rest.slice(0, slash);
  }
  let port: string | undefined;
  const colon = rest.lastIndexOf(':');
  if (colon >= 0) {
    port = rest.slice(colon + 1);
    rest = rest.slice(0, colon);
  }
  const wildcard = rest.startsWith('*.');
  const host = wildcard ? rest.slice(2) : rest;
  return { host, wildcard, port, path };
}

/** Returns a specificity score (> 0) if the URL matches the pattern, otherwise 0. */
export function scorePattern(pattern: string, url: URL): number {
  const p = parsePattern(pattern);
  const host = url.hostname.toLowerCase();
  if (p.port !== undefined && p.port !== url.port) return 0;

  let score: number;
  if (p.wildcard) {
    if (!host.endsWith('.' + p.host)) return 0;
    score = 500 + p.host.split('.').length;
  } else {
    if (host !== p.host) return 0;
    score = 1000;
  }

  if (p.path !== '/') {
    const path = url.pathname.toLowerCase();
    const matchesPath = path === p.path || path.startsWith(p.path.endsWith('/') ? p.path : p.path + '/');
    if (!matchesPath) return 0;
    score += p.path.length;
  }
  return score;
}

/** Picks the tool whose most specific pattern matches the URL. Earlier tools win ties. */
export function matchTool<T extends { match: string[] }>(tools: readonly T[], rawUrl: string): T | undefined {
  let url: URL;
  try {
    url = new URL(rawUrl);
  } catch {
    return undefined;
  }
  let best: T | undefined;
  let bestScore = 0;
  for (const tool of tools) {
    for (const pattern of tool.match) {
      const score = scorePattern(pattern, url);
      if (score > bestScore) {
        best = tool;
        bestScore = score;
      }
    }
  }
  return best;
}

/** True if the origin (scheme://host[:port]) is covered by any pattern's host part. */
export function originMatchesPatterns(origin: string, patterns: readonly string[]): boolean {
  let url: URL;
  try {
    url = new URL(origin);
  } catch {
    return false;
  }
  return patterns.some((pattern) => {
    const p = parsePattern(pattern);
    const hostPattern = (p.wildcard ? '*.' : '') + p.host + (p.port !== undefined ? ':' + p.port : '');
    return scorePattern(hostPattern, url) > 0;
  });
}

const REDACTED = 'ENTFERNT';

/** Replaces the values of sensitive query parameters and the fragment if it looks like it carries a token. */
export function redactUrl(rawUrl: string, sensitiveParams: readonly string[]): string {
  let url: URL;
  try {
    url = new URL(rawUrl);
  } catch {
    return rawUrl;
  }
  const sensitive = sensitiveParams.map((p) => p.toLowerCase());
  for (const key of [...url.searchParams.keys()]) {
    if (sensitive.includes(key.toLowerCase())) url.searchParams.set(key, REDACTED);
  }
  if (/(access_token|id_token|token|code)=/i.test(url.hash)) url.hash = REDACTED;
  return url.toString();
}

/**
 * Wildcard match where "*" stands for any sequence of characters. Iterative with a single
 * backtrack point: at most O(pattern × text) steps, so no pattern can freeze the page
 * (a regex like /.*a.*a.*a…/ can take seconds on long paths).
 */
export function wildcardMatch(pattern: string, text: string): boolean {
  let p = 0;
  let t = 0;
  let star = -1;
  let mark = 0;
  while (t < text.length) {
    if (p < pattern.length && pattern[p] === '*') {
      star = p++;
      mark = t;
    } else if (p < pattern.length && pattern[p] === text[t]) {
      p++;
      t++;
    } else if (star !== -1) {
      p = star + 1;
      t = ++mark;
    } else {
      return false;
    }
  }
  while (p < pattern.length && pattern[p] === '*') p++;
  return p === pattern.length;
}

/**
 * Path patterns for pages where the launcher stays hidden (case-insensitive):
 *   /wand        exactly this page (with or without trailing slash)
 *   /admin/*     everything below /admin, and /admin itself
 *   /docs/**     same as /docs/*
 *   *.pdf        any path ending in .pdf
 */
export function pathMatches(pattern: string, pathname: string): boolean {
  const path = (pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname).toLowerCase() || '/';
  let p = pattern.trim().toLowerCase().replace(/\*{2,}/g, '*');
  if (!p) return false;
  if (p.length > 1 && p.endsWith('/*')) {
    p = p.slice(0, -2) || '/';
    return wildcardMatch(p, path) || wildcardMatch(`${p === '/' ? '' : p}/*`, path);
  }
  return wildcardMatch(p.length > 1 ? p.replace(/\/+$/, '') : p, path);
}

export function isPathExcluded(patterns: readonly string[] | undefined, pathname: string): boolean {
  return !!patterns?.some((p) => pathMatches(p, pathname));
}
